import { Document, Page, View } from "@react-pdf/renderer";
import React from "react";
import { IntlProvider } from "react-intl";
import { styles } from "../reportingHelpers";
import MatchInfo from "./MatchInfo";
import Officials from "./Officials";
import ScoringLog from "./ScoringLog";
import SeasonHeader from "./SeasonHeader";
import TeamRoster from "./TeamRoster";
import TeamsAndScores from "./TeamsAndScores";
import {
  buildPlayerLookup,
  calculateCardsFromPbp,
  calculateScoringFromPbp,
  calculateSubsFromPbp,
  processScoringIntervals,
  scoresToScoreString,
} from "./helpers";

const HockeyMatchReport = ({ data, intl, locale }) => {
  const {
    fixture,
    fixtureProfile: { profile: fixtureProfile },
    playByPlay,
    roles,
    scoringIntervals,
    teams,
  } = data;
  const homeTeamId = Object.keys(teams).find((id) => teams[id].isHome);
  const awayTeamId = Object.keys(teams).find((id) => !teams[id].isHome);
  const playerLookup = {
    [homeTeamId]: buildPlayerLookup(teams[homeTeamId].players),
    [awayTeamId]: buildPlayerLookup(teams[awayTeamId].players),
  };
  const rolesLookup = {
    [homeTeamId]: buildPlayerLookup(
      roles.filter((r) => r.entityId === homeTeamId)
    ),
    [awayTeamId]: buildPlayerLookup(
      roles.filter((r) => r.entityId === awayTeamId)
    ),
  };
  const scoreStrBuilder = scoresToScoreString(homeTeamId, awayTeamId);
  const scoring = calculateScoringFromPbp(
    playByPlay,
    scoreStrBuilder,
    playerLookup,
    teams,
    fixtureProfile
  );
  const subs = calculateSubsFromPbp(playByPlay, fixtureProfile);
  const cards = calculateCardsFromPbp(playByPlay, fixtureProfile);
  const intervals = processScoringIntervals(
    scoringIntervals,
    homeTeamId,
    awayTeamId
  );

  return (
    <IntlProvider locale={locale ?? "en-US"}>
      <Document>
        <Page
          size="A4"
          orientation="portrait"
          style={{ ...styles.page, fontFamily: ["Noto Sans", "Noto Sans TC"] }}
          wrap={false}
        >
          <SeasonHeader fixture={fixture} />
          <MatchInfo
            intl={intl}
            fixture={fixture}
            matchLabel="reports.matchReport"
          />
          <TeamsAndScores
            intl={intl}
            scoringIntervals={intervals}
            teams={teams}
          />
          <View
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <TeamRoster
              intl={intl}
              roster={teams[homeTeamId].players}
              subs={subs[homeTeamId]}
              cards={cards[homeTeamId]}
              fixtureProfile={fixtureProfile}
              roles={rolesLookup[homeTeamId]}
            />
            <TeamRoster
              intl={intl}
              roster={teams[awayTeamId].players}
              subs={subs[awayTeamId]}
              cards={cards[awayTeamId]}
              fixtureProfile={fixtureProfile}
              roles={rolesLookup[awayTeamId]}
            />
          </View>
          <Officials roles={roles} />
          <ScoringLog intl={intl} scoring={scoring} />
        </Page>
      </Document>
    </IntlProvider>
  );
};

export default HockeyMatchReport;
